/**
 * The model's notes, read first and edited second.
 *
 * The old card is a permanent three-row textarea (`components/ModelNotes.tsx`, still what
 * the legacy Overview mounts). Two things were wrong with it and they compound. A field
 * that is always a form asks to be typed in and never asks to be read, so the notes on a
 * model nobody is currently editing are three lines of a scrollbar. And the field is not
 * only theirs: rgip-copilot merges two delimited blocks into the same string during a
 * build, so what a person actually saw was their own sentence followed by
 * `<!-- rgip:build-log 2026-09-02 -->` and a wall of machine prose.
 *
 * So: read view by default, rendered as markdown, with rgip's blocks lifted OUT of the
 * prose and drawn as what they are. The frame is what the assistant was told the model is
 * for. The log is the assistant's own record of the writes it made and why, which is the
 * thing worth reading on somebody else's model and was the least readable part of the
 * page. Editing opens a real editor over the human half only (`notesBlocks`), and saving
 * puts the blocks back untouched.
 *
 * Both sections are temporary in this shape. The build log is moving to its own store and
 * its own route, at which point this file loses the decisions section and notes go back to
 * being one person's prose.
 *
 * A third block, `rgip:decisions`, is what a person APPROVED for this model in the
 * assistant's Memory (rgip ADR 0027). rgip rebuilds it from its own store on every change,
 * so it is read-only here and edited from the chat's Memory view. It is drawn as its own
 * "Approved decisions" section, never mixed into the build log, and only when it exists.
 */
import { inject, observer } from 'mobx-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Form } from 'react-bootstrap';

import { PROFILES_PERMISSIONS } from '../../../permissions';
import Store from '../../../store';
import checkPermissions from '../CheckPermissions';
import { Markdown } from './ChatMarkdown';
import {
  humanNotes,
  readBuildLog,
  readDecisions,
  readFrame,
  withHumanNotes,
} from './notesBlocks';
import './modelNotes.css';

/** How many decisions are drawn before the list asks to be opened. */
const SHOWN = 4;

interface ModelNotesPanelProps {
  store?: Store;
}

export const ModelNotesPanel = inject('store')(
  observer(function ModelNotesPanel({ store }: ModelNotesPanelProps) {
    const notes = store.activeModel?.notes ?? '';
    const { isAllowedToEdit } = store;
    const saving = store.rootPage.updatingModelNotes;

    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [openLog, setOpenLog] = useState(false);
    const [failed, setFailed] = useState(false);

    const written = useMemo(() => humanNotes(notes), [notes]);
    const frame = useMemo(() => readFrame(notes), [notes]);
    const log = useMemo(() => readBuildLog(notes), [notes]);
    const approved = useMemo(() => readDecisions(notes), [notes]);

    // A model going live closes the editor rather than leaving an open form whose Save is
    // dead. `isAllowedToEdit` is the same browser-side flag the page header reads.
    useEffect(() => {
      if (!isAllowedToEdit) setEditing(false);
    }, [isAllowedToEdit]);

    const open = () => {
      setDraft(written);
      setFailed(false);
      setEditing(true);
    };

    const save = async () => {
      setFailed(false);
      try {
        // The whole field, not the draft: the route replaces it, so the blocks have to be
        // carried back or a person adding a sentence deletes the assistant's log.
        await store.saveModelNotes(withHumanNotes(notes, draft));
        setEditing(false);
      } catch {
        // Kept open, holding what they typed. The alternative is closing on a failed save
        // and showing them the old text as though it had been written.
        setFailed(true);
      }
    };

    // Newest first: the log is appended to, so the last line is the most recent decision
    // and the one somebody opening this model wants first.
    const entries = log ? [...log.entries].reverse() : [];
    const hidden = Math.max(0, entries.length - SHOWN);

    return (
      <Card border="light" className="mnp">
        <Card.Body>
          <div className="mnp-head">
            <h5 className="mnp-title">Model notes</h5>
            {!editing &&
              isAllowedToEdit &&
              checkPermissions(
                PROFILES_PERMISSIONS.ARCHITECT,
                <button type="button" className="mnp-edit" onClick={open}>
                  {written ? 'Edit' : 'Write a note'}
                </button>
              )}
          </div>

          {editing ? (
            <>
              <Form.Control
                as="textarea"
                className="mnp-input"
                rows={10}
                autoFocus
                value={draft}
                disabled={saving}
                onChange={(event) => setDraft(event.target.value)}
                aria-label="Model notes"
              />
              <p className="mnp-hint">
                Markdown. Anything the assistant wrote is kept and is not edited
                here.
              </p>
              {failed && (
                <p className="mnp-failed">
                  That did not save. Your text is still here, so try again.
                </p>
              )}
              <div className="mnp-actions">
                {checkPermissions(
                  PROFILES_PERMISSIONS.ARCHITECT,
                  <Button variant="primary" disabled={saving} onClick={save}>
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                )}
                <Button
                  variant="link"
                  disabled={saving}
                  onClick={() => setEditing(false)}
                >
                  Cancel
                </Button>
              </div>
            </>
          ) : written ? (
            <div className="mnp-prose">
              <Markdown text={written} />
            </div>
          ) : (
            <p className="mnp-empty">
              {isAllowedToEdit
                ? 'Nothing written yet. What should the next person know about this model?'
                : 'Nothing written yet.'}
            </p>
          )}

          {/* Written by the assistant, so it is labelled as such and sits apart from the
              prose above rather than being pasted into it. */}
          {frame && (
            <section className="mnp-block">
              <h6 className="mnp-block-title">
                What this model is for
                {frame.stamp && (
                  <span className="mnp-stamp">{frame.stamp}</span>
                )}
              </h6>
              <div className="mnp-prose mnp-prose--quiet">
                <Markdown text={frame.body} />
              </div>
            </section>
          )}

          {/* Approved by a person in Memory, so drawn whole: there are few, and each one
              is an instruction the next conversation follows. */}
          {approved && approved.entries.length > 0 && (
            <section
              className="mnp-block"
              data-testid="notes-approved-decisions"
            >
              <h6 className="mnp-block-title">
                Approved decisions
                <span className="mnp-count">{approved.entries.length}</span>
              </h6>
              <ol className="mnp-log">
                {approved.entries.map((entry, index) => (
                  <li key={`${index}:${entry.raw}`} className="mnp-entry">
                    {entry.date && (
                      <span className="mnp-subject">{entry.date}</span>
                    )}
                    <span className="mnp-why">{entry.text}</span>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {log && entries.length > 0 && (
            <section className="mnp-block">
              <h6 className="mnp-block-title">
                Decisions
                <span className="mnp-count">{entries.length}</span>
              </h6>
              <ol className="mnp-log">
                {(openLog ? entries : entries.slice(0, SHOWN)).map((entry) => (
                  <li key={entry.raw} className="mnp-entry">
                    {entry.subject && (
                      <span className="mnp-subject">{entry.subject}</span>
                    )}
                    <span className="mnp-why">{entry.text}</span>
                  </li>
                ))}
              </ol>
              {hidden > 0 && (
                <button
                  type="button"
                  className="mnp-more"
                  onClick={() => setOpenLog((was) => !was)}
                >
                  {openLog ? 'Show fewer' : `Show all ${entries.length}`}
                </button>
              )}
              {/* rgip caps its own block and says so. Repeating it is the difference
                  between a log that forgot and a log that looks complete. */}
              {log.trimmed > 0 && (
                <p className="mnp-trimmed">
                  {log.trimmed} earlier{' '}
                  {log.trimmed === 1 ? 'entry' : 'entries'} not kept
                </p>
              )}
            </section>
          )}
        </Card.Body>
      </Card>
    );
  })
);

export default ModelNotesPanel;
